"use strict";
/**
 * @license
 * Copyright Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.serializeTokens = exports.serializeToken = exports.escapeIdent = void 0;
/**
 * @fileoverview Exports methods for serializing CSS tokens.
 */
const tokens_js_1 = require("./tokens.js");
function escapeCodePoint(c) {
    return `\\${c.codePointAt(0).toString(16)} `;
}
function escapeString(str) {
    // We don't escape some characters to increase readability.
    return ('"' +
        str.replace(/[^A-Za-z0-9_/. :,?=%;-]/g, (c) => escapeCodePoint(c)) +
        '"');
}
/**
 * Escapes a CSS identifier.
 *
 * @param ident The identifier to escape.
 * @return The escaped identifier.
 */
function escapeIdent(ident) {
    // We don't generally escape digits or "-" in identifiers, however we do need
    // to do this for the first character to avoid ambiguity.
    //
    // For example, the string "123" would create a valid number token, but if
    // we want to have an ident-token, it needs to be escaped as a "\31 23".
    const firstChar = /^[^A-Za-z_]/.test(ident)
        ? escapeCodePoint(ident[0])
        : ident[0];
    return (firstChar +
        ident.slice(1).replace(/[^A-Za-z0-9_-]/g, (c) => escapeCodePoint(c)));
}
exports.escapeIdent = escapeIdent;
/**
 * Serializes a CSS token to a string.
 *
 * @param token The token to serialize.
 * @return The serialized token.
 */
function serializeToken(token) {
    switch (token.tokenKind) {
        case tokens_js_1.CssTokenKind.AT_KEYWORD:
            return `@${escapeIdent(token.name)}`;
        case tokens_js_1.CssTokenKind.CDC:
            return '-->';
        case tokens_js_1.CssTokenKind.CDO:
            return '<!--';
        case tokens_js_1.CssTokenKind.CLOSE_CURLY:
            return '}';
        case tokens_js_1.CssTokenKind.CLOSE_PAREN:
            return ')';
        case tokens_js_1.CssTokenKind.CLOSE_SQUARE:
            return ']';
        case tokens_js_1.CssTokenKind.COLON:
            return ':';
        case tokens_js_1.CssTokenKind.COMMA:
            return ',';
        case tokens_js_1.CssTokenKind.DELIM:
            // A <delim-token> containing U+005C REVERSE SOLIDUS (\) must be
            // serialized as U+005C REVERSE SOLIDUS followed by a newline. (The
            // tokenizer only ever emits such a token followed by a <whitespace-token>
            // that starts with a newline.)
            // Source: https://www.w3.org/TR/2021/CRD-css-syntax-3-20211224/#serialization
            if (token.codePoint === '\\') {
                return '\\\n';
            }
            return token.codePoint;
        case tokens_js_1.CssTokenKind.DIMENSION:
            return token.repr + escapeIdent(token.dimension);
        case tokens_js_1.CssTokenKind.EOF:
            return '';
        case tokens_js_1.CssTokenKind.FUNCTION:
            return escapeIdent(token.lowercaseName) + '(';
        case tokens_js_1.CssTokenKind.HASH:
            return '#' + escapeIdent(token.value);
        case tokens_js_1.CssTokenKind.IDENT:
            return escapeIdent(token.ident);
        case tokens_js_1.CssTokenKind.NUMBER:
            return token.repr;
        case tokens_js_1.CssTokenKind.OPEN_CURLY:
            return '{';
        case tokens_js_1.CssTokenKind.OPEN_PAREN:
            return '(';
        case tokens_js_1.CssTokenKind.OPEN_SQUARE:
            return '[';
        case tokens_js_1.CssTokenKind.PERCENTAGE:
            return token.repr + '%';
        case tokens_js_1.CssTokenKind.SEMICOLON:
            return ';';
        case tokens_js_1.CssTokenKind.STRING:
            return escapeString(token.value);
        case tokens_js_1.CssTokenKind.WHITESPACE:
            return ' ';
        default:
            checkExhaustive(token);
    }
}
exports.serializeToken = serializeToken;
/**
 * Serializes a list of CSS tokens to a string.
 *
 * @param tokens The tokens to serialize.
 * @return The serialized tokens.
 */
function serializeTokens(tokens) {
    // TODO(securitymb): Per spec [1] we should add an empty comment between certain
    // kinds of tokens. However, given that we're willfully violating the spec
    // by emitting a whitespace token for comments, we *probably* don't need to
    // worry about this.
    //
    // We should probably test it more thouroughly though, and implement the
    // empty comments if needed.
    //
    // [1]: https://www.w3.org/TR/css-syntax-3/#serialization:~:text=For%20any%20consecutive,(%2Dtoken
    return tokens.map(serializeToken).join('');
}
exports.serializeTokens = serializeTokens;
function checkExhaustive(value, msg = `unexpected value ${value}!`) {
    throw new Error(msg);
}
